<template>
  <div>
    <v-dialog
      width="90%"
      max-width="1350"
      v-model="show"
      @keydown.esc="cancelIfEditing"
    >
      <v-card v-if="projectID">
        <v-card-title>
          Managers history
          <v-spacer></v-spacer>
          <v-btn icon @click.prevent="show = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text>
          <v-data-table
            dense
            :headers="headers"
            :items="history[projectID] || []"
            :key="`update-table-${updateKey}`"
            @update:options="onTableOptionsChange"
          >
            <!-- Project coordinator (PMFullName) -->
            <template v-slot:[`item.PMFullName`]='{ item, index }'>
              <v-autocomplete v-if="editMode && currentRowIndex === index"
                dense
                hide-details
                clearable
                :items="employeeListWithDeleted"
                item-text="full_name"
                item-value="employee_id"
                v-model="currentItem.Project_Manager_ID"
              >
                <template v-slot:item="{ item, attrs, on }">
                  <v-list-item
                    v-bind="attrs"
                    v-on="on"
                    :class="{ 'deleted-employee': item.is_deleted }"
                  >
                    <v-list-item-content>
                      <v-list-item-title>{{ item.full_name }}</v-list-item-title>
                      <v-list-item-subtitle v-if="item.is_deleted">
                        (Deleted)
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </template>
                <template v-slot:selection="{ item }">
                  <span :class="{ 'deleted-employee-text': item.is_deleted }">
                    {{ item.full_name }}
                    <span v-if="item.is_deleted"></span>
                  </span>
                </template>
              </v-autocomplete>
              <span
                @click="openEmployeeCard(item.Project_Manager_ID)"
                style="cursor: pointer; text-decoration: underline;"
                :class="{ 'deleted-employee-text': isEmployeeDeleted(item.Project_Manager_ID) }"
                v-else
              >
                {{ item.PMFullName }}
                <span v-if="isEmployeeDeleted(item.Project_Manager_ID)"></span>
              </span>
            </template>
            <!-- /Project coordinator -->

            <!-- Project manager (SBUFullName) -->
            <template v-slot:[`item.SBUFullName`]='{ item, index }'>
              <v-autocomplete v-if="editMode && currentRowIndex === index"
                dense
                hide-details
                clearable
                :items="employeeListWithDeleted"
                item-text="full_name"
                item-value="employee_id"
                v-model="currentItem.SBU_ID"
              >
                <template v-slot:item="{ item, attrs, on }">
                  <v-list-item
                    v-bind="attrs"
                    v-on="on"
                    :class="{ 'deleted-employee': item.is_deleted }"
                  >
                    <v-list-item-content>
                      <v-list-item-title>{{ item.full_name }}</v-list-item-title>
                      <v-list-item-subtitle v-if="item.is_deleted">
                        (Deleted)
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </template>
                <template v-slot:selection="{ item }">
                  <span :class="{ 'deleted-employee-text': item.is_deleted }">
                    {{ item.full_name }}
                    <span v-if="item.is_deleted"></span>
                  </span>
                </template>
              </v-autocomplete>
              <span
                @click="openEmployeeCard(item.SBU_ID)"
                style="cursor: pointer; text-decoration: underline;"
                :class="{ 'deleted-employee-text': isEmployeeDeleted(item.SBU_ID) }"
                v-else
              >
                {{ item.SBUFullName }}
                <span v-if="isEmployeeDeleted(item.SBU_ID)"></span>
              </span>
            </template>
            <!-- /Project manager -->

            <!-- Account manager -->
            <template v-slot:[`item.AMFullName`]='{ item, index }'>
              <v-autocomplete v-if="editMode && currentRowIndex === index"
                dense
                hide-details
                clearable
                :items="employeeListWithDeleted"
                item-text="full_name"
                item-value="employee_id"
                v-model="currentItem.Account_Manager_ID"
              >
                <template v-slot:item="{ item, attrs, on }">
                  <v-list-item
                    v-bind="attrs"
                    v-on="on"
                    :class="{ 'deleted-employee': item.is_deleted }"
                  >
                    <v-list-item-content>
                      <v-list-item-title>{{ item.full_name }}</v-list-item-title>
                      <v-list-item-subtitle v-if="item.is_deleted">
                        (Deleted)
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </template>
                <template v-slot:selection="{ item }">
                  <span :class="{ 'deleted-employee-text': item.is_deleted }">
                    {{ item.full_name }}
                    <span v-if="item.is_deleted"></span>
                  </span>
                </template>
              </v-autocomplete>
              <span
                @click="openEmployeeCard(item.Account_Manager_ID)"
                style="cursor: pointer; text-decoration: underline;"
                :class="{ 'deleted-employee-text': isEmployeeDeleted(item.Account_Manager_ID) }"
                v-else
              >
                {{ item.AMFullName }}
                <span v-if="isEmployeeDeleted(item.Account_Manager_ID)"></span>
              </span>
            </template>
            <!-- /Account manager -->

            <!-- Project administrator -->
            <template v-slot:[`item.PAFullName`]='{ item, index }'>
              <v-autocomplete v-if="editMode && currentRowIndex === index"
                dense
                clearable
                hide-details
                :items="employeeListWithDeleted"
                item-text="full_name"
                item-value="employee_id"
                v-model="currentItem.Project_Administrator_ID"
              >
                <template v-slot:item="{ item, attrs, on }">
                  <v-list-item
                    v-bind="attrs"
                    v-on="on"
                    :class="{ 'deleted-employee': item.is_deleted }"
                  >
                    <v-list-item-content>
                      <v-list-item-title>{{ item.full_name }}</v-list-item-title>
                      <v-list-item-subtitle v-if="item.is_deleted">
                        (Deleted)
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </template>
                <template v-slot:selection="{ item }">
                  <span :class="{ 'deleted-employee-text': item.is_deleted }">
                    {{ item.full_name }}
                    <span v-if="item.is_deleted"></span>
                  </span>
                </template>
              </v-autocomplete>
              <span
                @click="openEmployeeCard(item.Project_Administrator_ID)"
                style="cursor: pointer; text-decoration: underline;"
                :class="{ 'deleted-employee-text': isEmployeeDeleted(item.Project_Administrator_ID) }"
                v-else
              >
                {{ item.PAFullName }}
                <span v-if="isEmployeeDeleted(item.Project_Administrator_ID)"></span>
              </span>
            </template>
            <!-- /Project administrator -->

            <!-- Period from -->
            <template v-slot:[`item.Period_From`]='{ item, index }'>
              <DatePicker
                v-if="editMode && currentRowIndex === index"
                v-model="currentItem.Period_From"
                :min="periodFromMin"
                :max="periodFromMax"
                dense
              />
              <span v-else>
                {{ item.Period_From }}
              </span>
            </template>
            <!-- /Period from -->

            <!-- Period To -->
            <template v-slot:[`item.Period_To`]='{ item, index }'>
              <DatePicker
                v-if="editMode && currentRowIndex === index"
                v-model="currentItem.Period_To"
                :min="periodToMin"
                :max="periodToMax"
                dense
              />
              <span v-else>
                {{ item.Period_To }}
              </span>
            </template>
            <!-- /Period to -->

            <!-- Edit -->
            <template v-slot:[`item.edit`]='{ item, index }' v-if="!notFromProjectsModule">
              <!-- Edit/save button -->
              <v-tooltip bottom :color="editMode ? 'success': 'primary'" v-if="canEdit">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    :disabled="(editMode && index !== currentRowIndex) || (editMode && !canSave) || addNewMode"
                    small
                    v-bind="attrs"
                    v-on="on"
                    icon
                    :color="editMode ? 'success': 'primary'"
                    @click.prevent="onEditClick(item, index)"
                  >
                    <v-icon>{{ editMode && index === currentRowIndex ? 'mdi-check-circle': 'mdi-pencil-circle' }}</v-icon>
                  </v-btn>
                </template>
                <span>{{ editMode ? 'Save': 'Edit' }}</span>
              </v-tooltip>
              <!-- /Edit/save button -->

              <!-- Delete button -->
              <v-tooltip bottom color="red darken-2" v-if="!editMode || (editMode && currentRowIndex !== index) && canDelete">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    small
                    :disabled="addNewMode || editMode && currentRowIndex !== index"
                    v-bind="attrs"
                    v-on="on"
                    icon color="red darken-2"
                    @click.prevent="openDeleteDialog(item)"
                  >
                    <v-icon>mdi-delete-circle</v-icon>
                  </v-btn>
                </template>
                <span>Delete</span>
              </v-tooltip>
              <!-- /Delete button -->

              <!-- Cancel button -->
              <v-tooltip bottom color="primary" v-if="editMode && index === currentRowIndex">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    small
                    v-bind="attrs"
                    v-on="on"
                    icon color="primary"
                    @click.prevent="onCancel"
                  >
                    <v-icon>mdi-close-circle</v-icon>
                  </v-btn>
                </template>
                <span>Cancel</span>
              </v-tooltip>
              <!-- /Cancel button -->
            </template>
            <!-- /Edit -->

            <!-- Add new row -->
            <template v-slot:[`body.append`] v-if="canEdit && !notFromProjectsModule">
              <!-- 1st TD: Project manager (SBU_ID) -->
              <td
                class="text-center px-2 text-no-wrap"
                :class="{'v-data-table__divider': addNewMode}"
              >
                <v-autocomplete v-if="addNewMode"
                  dense
                  hide-details
                  clearable
                  :items="employeeListWithDeleted"
                  item-text="full_name"
                  item-value="employee_id"
                  v-model="currentItem.SBU_ID"
                >
                  <template v-slot:item="{ item, attrs, on }">
                    <v-list-item
                      v-bind="attrs"
                      v-on="on"
                      :class="{ 'deleted-employee': item.is_deleted }"
                    >
                      <v-list-item-content>
                        <v-list-item-title>{{ item.full_name }}</v-list-item-title>
                        <v-list-item-subtitle v-if="item.is_deleted">
                          (Deleted)
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                  </template>
                  <template v-slot:selection="{ item }">
                    <span :class="{ 'deleted-employee-text': item.is_deleted }">
                      {{ item.full_name }}
                      <span v-if="item.is_deleted"></span>
                    </span>
                  </template>
                </v-autocomplete>
              </td>
              <!-- /Project manager -->

              <!-- 2nd TD: Project coordinator (Project_Manager_ID) -->
              <td
                class="text-center px-2 text-no-wrap"
                :class="{'v-data-table__divider': addNewMode}"
              >
                <v-autocomplete v-if="addNewMode"
                  dense
                  hide-details
                  clearable
                  :items="employeeListWithDeleted"
                  item-text="full_name"
                  item-value="employee_id"
                  v-model="currentItem.Project_Manager_ID"
                >
                  <template v-slot:item="{ item, attrs, on }">
                    <v-list-item
                      v-bind="attrs"
                      v-on="on"
                      :class="{ 'deleted-employee': item.is_deleted }"
                    >
                      <v-list-item-content>
                        <v-list-item-title>{{ item.full_name }}</v-list-item-title>
                        <v-list-item-subtitle v-if="item.is_deleted">
                          (Deleted)
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                  </template>
                  <template v-slot:selection="{ item }">
                    <span :class="{ 'deleted-employee-text': item.is_deleted }">
                      {{ item.full_name }}
                      <span v-if="item.is_deleted"></span>
                    </span>
                  </template>
                </v-autocomplete>
              </td>
              <!-- /Project coordinator -->

              <!-- Account manager -->
              <td
                class="text-center px-2 text-no-wrap"
                :class="{'v-data-table__divider': addNewMode}"
              >
                <v-autocomplete v-if="addNewMode"
                  dense
                  hide-details
                  clearable
                  :items="employeeListWithDeleted"
                  item-text="full_name"
                  item-value="employee_id"
                  v-model="currentItem.Account_Manager_ID"
                >
                  <template v-slot:item="{ item, attrs, on }">
                    <v-list-item
                      v-bind="attrs"
                      v-on="on"
                      :class="{ 'deleted-employee': item.is_deleted }"
                    >
                      <v-list-item-content>
                        <v-list-item-title>{{ item.full_name }}</v-list-item-title>
                        <v-list-item-subtitle v-if="item.is_deleted">
                          (Deleted)
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                  </template>
                  <template v-slot:selection="{ item }">
                    <span :class="{ 'deleted-employee-text': item.is_deleted }">
                      {{ item.full_name }}
                      <span v-if="item.is_deleted"></span>
                    </span>
                  </template>
                </v-autocomplete>
              </td>
              <!-- /Account manager -->

              <!-- Project administrator -->
              <td
                class="text-center px-2 text-no-wrap"
                :class="{'v-data-table__divider': addNewMode}"
              >
                <v-autocomplete v-if="addNewMode"
                  dense
                  hide-details
                  :items="employeeListWithDeleted"
                  item-text="full_name"
                  item-value="employee_id"
                  v-model="currentItem.Project_Administrator_ID"
                >
                  <template v-slot:item="{ item, attrs, on }">
                    <v-list-item
                      v-bind="attrs"
                      v-on="on"
                      :class="{ 'deleted-employee': item.is_deleted }"
                    >
                      <v-list-item-content>
                        <v-list-item-title>{{ item.full_name }}</v-list-item-title>
                        <v-list-item-subtitle v-if="item.is_deleted">
                          (Deleted)
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                  </template>
                  <template v-slot:selection="{ item }">
                    <span :class="{ 'deleted-employee-text': item.is_deleted }">
                      {{ item.full_name }}
                      <span v-if="item.is_deleted"></span>
                    </span>
                  </template>
                </v-autocomplete>
              </td>
              <!-- /Project administrator -->

              <!-- Period from -->
              <td
                class="text-center px-2 text-no-wrap"
                :class="{'v-data-table__divider': addNewMode}"
              >
                <DatePicker
                  v-if="addNewMode"
                  v-model="currentItem.Period_From"
                  :min="minAllowedDate"
                  :max="null"
                  dense
                  @onDatePickerChange="onPeriodFromChange()"
                />
              </td>
              <!-- /Period from -->

              <!-- Period to -->
              <td
                class="text-center px-2 text-no-wrap"
                :class="{'v-data-table__divider': addNewMode}"
              >
                <DatePicker
                  v-if="addNewMode"
                  v-model="currentItem.Period_To"
                  :min="currentItem.Period_From || minAllowedDate"
                  :max="null"
                  dense
                />
              </td>
              <!-- /Period to -->

              <!-- Add/save/cancel -->
              <td class="text-center px-2 text-no-wrap">
                <!-- Add/save -->
                <v-tooltip bottom color="success" v-if="!notFromProjectsModule">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      small
                      icon
                      :disabled="editMode || addNewMode && !canSave"
                      color="success"
                      v-bind="attrs"
                      v-on="on"
                      @click.prevent="addNew"
                    >
                      <v-icon>{{ addNewMode ? 'mdi-check-circle' :'mdi-plus-circle' }}</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ addNewMode ? 'Save' : 'Add new' }}</span>
                </v-tooltip>
                <!-- /Add/save -->

                <!-- Cancel -->
                <v-tooltip bottom color="primary" v-if="addNewMode">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      small
                      icon
                      color="primary"
                      v-bind="attrs"
                      v-on="on"
                      @click.prevent="onCancel"
                    >
                      <v-icon>mdi-close-circle</v-icon>
                    </v-btn>
                  </template>
                  <span>Cancel</span>
                </v-tooltip>
              </td>
              <!-- /Cancel -->
            </template>
            <!-- /Add new row -->
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-dialog>
    <ConfirmOrDeniedDialog
      v-if="removeDialog"
      v-model="removeDialog"
      :mode="confirmOrDeniedDialogModes.delete"
      :message="'You can\'t restore data after delete'"
      @onDeleteClick="deleteManagers"
      @onCancelClick="removeDialog = false"
      @onCloseClick="removeDialog = false"
    />
  </div>
</template>

<script>
import moment from 'moment'
import { getApiErrorMessage } from '@/utils/apiError'
import { formatLocalDate } from '@/utils/dateUtils'

export default {
  name: 'ManagersHistory',
  components: {
    DatePicker: () => import('@/components/global/DatePicker.vue'),
    ConfirmOrDeniedDialog: () => import('@/components/global/ConfirmOrDeniedDialog.vue')
  },
  props: {
    value: {
      required: false,
      default: false
    },
    projectID: {
      required: true,
      default: null
    },
    minDate: {
      required: false
    },
    notFromProjectsModule: {
      required: false,
      default: false
    }
  },
  data () {
    return {
      editMode: false,
      addNewMode: false,
      currentItem: null,
      currentRowIndex: null,
      currentOriginalItem: null,
      updateKey: 0,
      removeDialog: false,
      rolesOfCurrentUser: [],
      deletedEmployees: new Set(),
      minAllowedDate: '1900-01-01',
      maxAllowedDate: '2999-12-31'
    }
  },
  computed: {
    headers () {
      const headers = [
        {
          text: 'Project Manager',
          value: 'SBUFullName',
          divider: true,
          class: 'px-2 text-no-wrap',
          cellClass: 'px-2 text-no-wrap'
        },
        {
          text: 'Project Coordinator',
          value: 'PMFullName',
          divider: true,
          class: 'px-2 text-no-wrap',
          cellClass: 'px-2 text-no-wrap'
        },
        {
          text: 'Account Manager',
          value: 'AMFullName',
          divider: true,
          class: 'px-2 text-no-wrap',
          cellClass: 'px-2 text-no-wrap'
        },
        {
          text: 'Project Administrator',
          value: 'PAFullName',
          divider: true,
          class: 'px-2 text-no-wrap',
          cellClass: 'px-2 text-no-wrap'
        },
        {
          text: 'Period From',
          value: 'Period_From',
          divider: true,
          class: 'px-2 text-no-wrap',
          cellClass: 'px-2 text-no-wrap'
        },
        {
          text: 'Period To',
          value: 'Period_To',
          class: 'px-2 text-no-wrap',
          divider: this.canEdit && !this.notFromProjectsModule,
          cellClass: 'px-2 text-no-wrap'
        }
      ]
      if (this.canEdit && !this.notFromProjectsModule) {
        headers.push({
          text: '#',
          value: 'edit',
          align: 'center',
          class: 'px-2',
          cellClass: 'px-2 text-no-wrap'
        })
      }
      return headers
    },
    show: {
      get () {
        return this.value
      },
      set (val) {
        this.$emit('input', val)
      }
    },
    user () {
      return this.$store.getters.getUser
    },
    history () {
      return this.$store.getters.getProjectManagementHistory
    },
    employeeList () {
      return this.$store.getters.getEmployee || []
    },
    employeeListWithDeleted () {
      const list = this.employeeList || []
      const history = this.history[this.projectID] || []

      const employeeMap = new Map()

      list.forEach(emp => {
        employeeMap.set(emp.employee_id, emp)
      })

      history.forEach(item => {
        const employees = [
          { id: item.Project_Manager_ID, name: item.PMFullName },
          { id: item.SBU_ID, name: item.SBUFullName },
          { id: item.Account_Manager_ID, name: item.AMFullName },
          { id: item.Project_Administrator_ID, name: item.PAFullName }
        ]

        employees.forEach(emp => {
          if (emp.id && !employeeMap.has(emp.id)) {
            employeeMap.set(emp.id, {
              employee_id: emp.id,
              full_name: emp.name,
              is_deleted: true
            })
          }
        })
      })

      return Array.from(employeeMap.values())
    },

    periodFromMin () {
      if (!this.editMode || !this.currentOriginalItem) return this.minAllowedDate

      const history = this.history[this.projectID] || []
      const realIndex = history.findIndex(item =>
        item.Manager_ID === this.currentOriginalItem.Manager_ID
      )

      if (realIndex === -1) return this.minAllowedDate

      if (realIndex === history.length - 1) {
        return this.minAllowedDate
      }

      const prevRow = history[realIndex + 1]
      if (prevRow && prevRow.Period_To) {
        return moment(prevRow.Period_To).add(1, 'days').format('YYYY-MM-DD')
      }

      return this.minAllowedDate
    },

    periodFromMax () {
      if (!this.editMode || !this.currentOriginalItem) return null

      const history = this.history[this.projectID] || []
      const realIndex = history.findIndex(item =>
        item.Manager_ID === this.currentOriginalItem.Manager_ID
      )

      if (realIndex === -1) return null

      if (realIndex === history.length - 1) {
        return this.currentOriginalItem.Period_From
      }

      if (realIndex === 0) {
        return this.currentOriginalItem.Period_To
      }

      const nextRow = history[realIndex - 1]
      if (nextRow && nextRow.Period_From) {
        return moment(nextRow.Period_From).subtract(1, 'days').format('YYYY-MM-DD')
      }

      return null
    },

    periodToMin () {
      if (!this.editMode || !this.currentOriginalItem) return this.minAllowedDate

      const history = this.history[this.projectID] || []
      const realIndex = history.findIndex(item =>
        item.Manager_ID === this.currentOriginalItem.Manager_ID
      )

      if (realIndex === -1) return this.minAllowedDate

      if (realIndex === history.length - 1) {
        return this.currentItem.Period_From || this.currentOriginalItem.Period_From
      }

      return this.currentItem.Period_From || this.currentOriginalItem.Period_From
    },

    periodToMax () {
      if (!this.editMode || !this.currentOriginalItem) return this.maxAllowedDate

      const history = this.history[this.projectID] || []
      const realIndex = history.findIndex(item =>
        item.Manager_ID === this.currentOriginalItem.Manager_ID
      )

      if (realIndex === -1) return this.maxAllowedDate

      if (realIndex === 0) {
        return null
      }

      if (realIndex === history.length - 1) {
        const nextRow = history[realIndex - 1]
        if (nextRow && nextRow.Period_From) {
          return moment(nextRow.Period_From).subtract(1, 'days').format('YYYY-MM-DD')
        }
        return this.maxAllowedDate
      }

      const nextRow = history[realIndex - 1]
      if (nextRow && nextRow.Period_From) {
        return moment(nextRow.Period_From).subtract(1, 'days').format('YYYY-MM-DD')
      }

      return this.maxAllowedDate
    },

    canEdit () {
      let res = false
      for (let i = 0; i < this.rolesOfCurrentUser.length; i++) {
        if (['manager', 'financier', 'hr'].indexOf(this.rolesOfCurrentUser[i].RoleName.toLowerCase()) > -1) {
          res = true
          break
        }
      }
      return res
    },
    confirmOrDeniedDialogModes () {
      return this.$store.getters.getConfirmOrDeniedDialogModes
    },
    canDelete () {
      return this.canEdit
    },
    canSave () {
      return (this.currentItem.Project_Manager_ID ||
             this.currentItem.SBU_ID ||
             this.currentItem.Account_Manager_ID ||
             this.currentItem.Project_Administrator_ID) &&
             (this.currentItem.Period_From &&
             this.currentItem.Period_To)
    }
  },
  async mounted () {
    await this.getRolesOfCurrentUser()
    if (!this.history[this.projectID]) {
      this.getHistory()
    }
    this.getEmployeeList()
  },
  methods: {
    onPeriodFromChange () {
      if (this.currentItem.Period_To === null && this.currentItem.Period_From !== null) {
        this.currentItem.Period_To = moment(this.currentItem.Period_From).add(5, 'years').format('YYYY-MM-DD')
      }
    },
    async getRolesOfCurrentUser () {
      await this.axios.get('/api/employee_role/' + this.user.login, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(result => {
        this.rolesOfCurrentUser = result.data
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    openEmployeeCard (employeeID) {
      this.axios.get(`/api/employee/${employeeID}`, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        this.$store.dispatch('employeeDetailsScreen', {
          show: true,
          employee: res.data[0],
          edit: false
        })
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    openDeleteDialog (item) {
      this.currentItem = Object.assign({}, item)
      this.removeDialog = true
    },
    addNew () {
      this.currentRowIndex = this.history[this.projectID].length
      if (this.addNewMode) {
        this.addNewManagers()
      } else {
        this.currentItem = {
          Manager_ID: null,
          Project_ID: this.projectID,
          Period_From: null,
          Period_To: null,
          Project_Manager_ID: null,
          Project_Manager_Fee: 0,
          SBU_ID: null,
          SBU_FEE: 0,
          Account_Manager_ID: null,
          Project_Administrator_ID: null,
          AMFullName: '',
          PAFullName: '',
          PMFullName: '',
          SBUFullName: ''
        }
        this.addNewMode = !this.addNewMode
      }
    },
    onEditClick (item, index) {
      if (this.editMode && this.currentOriginalItem &&
          this.currentOriginalItem.Manager_ID !== item.Manager_ID) {
        this.cancelEditing()
      }

      if (this.editMode && this.currentOriginalItem &&
          this.currentOriginalItem.Manager_ID === item.Manager_ID) {
        this.updateManagers()
      } else {
        this.currentItem = Object.assign({}, item)
        this.currentOriginalItem = item
        this.currentRowIndex = index
        this.editMode = true
      }
    },
    onCancel () {
      this.cancelEditing()
    },
    cancelEditing () {
      this.editMode = false
      this.addNewMode = false
      this.currentOriginalItem = null
      this.currentItem = null
      this.currentRowIndex = null
    },
    onTableOptionsChange () {
      if (this.editMode) {
        this.cancelEditing()
      }
    },
    cancelIfEditing () {
      if (this.editMode) {
        this.cancelEditing()
      }
    },
    updateManagers () {
      this.axios.put(`/api/managers_history/${this.currentItem.Manager_ID}`, this.currentItem, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(response => {
        this.getHistory()
        this.cancelEditing()
        this.$store.dispatch('setSuccess', { successMsg: 'Managers successfully updated' })
        this.$emit('managersHistoryUpdated')
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    addNewManagers () {
      this.axios.post('/api/managers_history', this.currentItem, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(response => {
        this.getHistory()
        this.$store.dispatch('setSuccess', { successMsg: 'Managers successfully added' })
        this.$emit('managersHistoryUpdated')
        this.addNewMode = !this.addNewMode
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    deleteManagers () {
      this.axios.delete(`/api/managers_history/${this.currentItem.Manager_ID}`, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(response => {
        this.getHistory()
        this.$store.dispatch('setSuccess', { successMsg: 'Managers successfully deleted' })
        this.removeDialog = false
        this.$emit('managersHistoryUpdated')
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    getHistory () {
      this.axios.get(`/api/managers_history/${this.projectID}`, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(response => {
        response.data.forEach(item => {
          item.Period_From = formatLocalDate(item.Period_From)
          item.Period_To = formatLocalDate(item.Period_To)
        })
        this.$store.dispatch('setManagersForProject', {
          projectId: this.projectID,
          managers: response.data
        })
        this.updateKey++
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    getEmployeeList () {
      this.axios.get('/api/employeelist', {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(response => {
        this.deletedEmployees.clear()
        response.data.forEach(emp => {
          if (emp.is_deleted) {
            this.deletedEmployees.add(emp.employee_id)
          }
        })
        this.$store.dispatch('setEmployeeList', { employeeList: response.data })
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    isEmployeeDeleted (employeeId) {
      return this.deletedEmployees.has(employeeId)
    }
  }
}
</script>

<style scoped>
.deleted-employee {
  opacity: 0.6;
  background-color: #f5f5f5;
}

.deleted-employee-text {
  color: #999;
}
</style>
